import { useEffect, useRef, useState, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { AlertTriangle, Check, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { localeTagFor } from "@/i18n/locale";
import type { Provider, UserConnection } from "@/hooks/useConnections";
import { ConnectionBrandMark } from "@/components/connections/ConnectionBrandMark";
import ConnectionManageSurface from "@/components/connections/ConnectionManageSurface";

interface ConnectionCardProps {
  provider: Provider;
  /** Legacy icon prop kept so old call sites/tests do not need a big-bang rewrite. */
  icon?: unknown;
  /** Optional custom brand mark. Defaults to the provider's official/recognizable mark. */
  brand?: ReactNode;
  title: string;
  description: string;
  summary?: string;
  stateUnavailable?: boolean;
  connection: UserConnection | null;
  activityCount?: number | null;
  busy?: boolean;
  onConnect: () => void | Promise<void>;
  onSync: () => void | Promise<void>;
  onDisconnect: () => void | Promise<void>;
  syncing?: boolean;
  connecting?: boolean;
  i18nPrefix?: string;
  /** Legacy visual props: kept for API compatibility; the catalog now uses brand marks. */
  iconColorClass?: string;
  iconBgClass?: string;
  connectBgClass?: string;
  onViewData?: () => void;
  viewDataLabel?: string;
  missingPermissions?: string[];
  onFixPermissions?: () => void | Promise<void>;
  fixPermissionsLabel?: string;
  permissionHint?: string;
}

export default function ConnectionCard({
  provider,
  brand,
  title,
  description,
  summary,
  stateUnavailable = false,
  connection,
  activityCount,
  busy = false,
  onConnect,
  onSync,
  onDisconnect,
  syncing = false,
  connecting = false,
  i18nPrefix = "strava",
  onViewData,
  viewDataLabel,
  missingPermissions = [],
  onFixPermissions,
  fixPermissionsLabel,
  permissionHint,
}: ConnectionCardProps) {
  const { t, lang } = useLanguage();
  const reduceMotion = useReducedMotion();
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState("");
  const [manageOpen, setManageOpen] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const actionLock = useRef(false);

  useEffect(() => {
    if (stateUnavailable) {
      setManageOpen(false);
      setDisconnectDialogOpen(false);
    }
  }, [stateUnavailable]);

  const runAction = async (action: () => void | Promise<void>) => {
    if (actionLock.current || busy || stateUnavailable) return false;
    actionLock.current = true;
    setWorking(true);
    setActionError("");
    try {
      await action();
      return true;
    } catch (cause) {
      console.warn("[ConnectionCard] action failed", cause);
      setActionError(cause instanceof Error && cause.message === "provider_not_configured"
        ? "expansion.disabled" : "connections.ux.actionFailed");
      return false;
    } finally {
      actionLock.current = false;
      setWorking(false);
    }
  };

  const status = connection?.status ?? null;
  const stateExpiry = connection?.oauth_state_expires_at
    ? Date.parse(connection.oauth_state_expires_at)
    : NaN;
  const pendingInFlight = status === "pending" && Number.isFinite(stateExpiry) && stateExpiry > Date.now();
  const pendingDead = status === "pending" && !pendingInFlight;
  const needsReconnect = status === "expired" || status === "error" || status === "revoked";
  const lastSyncStatus = connection?.last_sync_status ?? null;
  const permissionProblem = status === "active" && missingPermissions.length > 0;
  const syncEmpty = status === "active" && !permissionProblem && lastSyncStatus === "empty";
  const syncProblem = permissionProblem || (
    status === "active" &&
    lastSyncStatus !== null &&
    lastSyncStatus !== "ok" &&
    lastSyncStatus !== "success" &&
    lastSyncStatus !== "empty"
  );
  const oneDriveUnlicensed = provider === "onedrive" && connection?.last_sync_error === "onedrive_license_required";
  const teamsAccessDenied = provider === "microsoft_teams" && connection?.last_sync_error === "provider_http_403";
  const scopeProblem = syncProblem && !teamsAccessDenied && /\b403\b/.test(connection?.last_sync_error ?? "");
  const attention = !stateUnavailable && (needsReconnect || syncProblem || pendingInFlight || pendingDead);
  const isConnected = !stateUnavailable && status === "active";
  const healthyConnected = isConnected && !attention && !syncEmpty;
  const mark = brand ?? <ConnectionBrandMark provider={provider} />;

  const statusKey = stateUnavailable ? "connections.ux.statusUnknown" : needsReconnect || syncProblem
    ? "connections.ux.attention"
    : status === "active"
      ? "connections.ux.connected"
      : status === "pending"
        ? "connections.ux.pending"
        : "connections.ux.notConnected";

  const handleDisconnectConfirm = async () => {
    if (await runAction(onDisconnect)) {
      setDisconnectDialogOpen(false);
      setManageOpen(false);
    }
  };

  const summaryConnect = async () => {
    await runAction(onConnect);
  };

  const details = (
    <div className="space-y-4 pt-1">
      <div className="flex items-center justify-between gap-3 rounded-xl border border-border/70 bg-muted/30 px-3 py-2.5">
        <span className="text-sm text-muted-foreground">{t("connections.ux.status")}</span>
        <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${attention ? "text-amber-500" : healthyConnected ? "text-emerald-500" : "text-muted-foreground"}`}>
          {attention ? <AlertTriangle className="h-4 w-4" /> : healthyConnected ? <Check className="h-4 w-4" /> : null}
          {t(statusKey)}
        </span>
      </div>

      {actionError && <p role="alert" className="text-sm text-destructive">{t(actionError)}</p>}

      {status === "active" && (
        <div className="space-y-3">
          {onViewData && viewDataLabel && (
            <button
              type="button"
              onClick={() => { setManageOpen(false); onViewData(); }}
              className="w-full rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary/15"
            >
              {viewDataLabel}
            </button>
          )}

          <div className="rounded-xl border border-border/70 bg-card px-3 py-3 text-sm">
            {activityCount != null && (
              <p className="text-muted-foreground">
                {activityCount === 0
                  ? !syncEmpty && t(`${i18nPrefix}.connections.zeroActivities`)
                  : <><span className="font-medium text-foreground">{activityCount}</span>{" "}{t(`${i18nPrefix}.connections.activitiesImported`)}</>}
              </p>
            )}
            <p className="mt-1 text-muted-foreground">
              {t(`${i18nPrefix}.connections.lastSync`)}:{" "}
              <span className="text-foreground">
                {connection?.last_sync_at && Number.isFinite(Date.parse(connection.last_sync_at))
                  ? new Intl.DateTimeFormat(localeTagFor(lang), { dateStyle: "medium", timeStyle: "short" }).format(new Date(connection.last_sync_at))
                  : t(`${i18nPrefix}.connections.never`)}
              </span>
            </p>
          </div>

          {syncEmpty && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-3 space-y-1">
              <p className="text-sm text-foreground">{t("connections.syncEmpty")}</p>
              <p className="text-xs text-muted-foreground">{t("connections.syncEmptyHint")}</p>
            </div>
          )}

          {syncProblem && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-3 space-y-2">
              {permissionProblem ? (
                <p className="text-sm text-destructive">{t("connections.permissionPartial")} {missingPermissions.join(", ")}.</p>
              ) : (
                <p className="text-sm text-destructive">{t(oneDriveUnlicensed ? "connections.oneDriveLicense" : teamsAccessDenied ? "expansion.teamsAccessDenied" : "connections.syncProblem")}</p>
              )}
              {connection?.last_sync_error && (
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer py-1">{t("connections.ux.details")}</summary>
                  <p className="break-words pt-2">{connection.last_sync_error}</p>
                </details>
              )}
              {scopeProblem && <p className="text-xs text-destructive/90">{t("connections.reconnectHint")}</p>}
              {permissionProblem && onFixPermissions && fixPermissionsLabel && (
                <button
                  type="button"
                  onClick={() => { void runAction(onFixPermissions); }}
                  disabled={working || busy}
                  className="w-full rounded-lg bg-destructive/90 px-3 py-2.5 text-sm font-medium text-destructive-foreground disabled:opacity-50"
                >
                  {fixPermissionsLabel}
                </button>
              )}
              {permissionProblem && permissionHint && <p className="text-xs text-muted-foreground">{permissionHint}</p>}
            </div>
          )}

          <button
            type="button"
            onClick={() => { void runAction(onSync); }}
            disabled={syncing || busy || working}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-medium text-primary-foreground disabled:opacity-50"
          >
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {syncing ? t(`${i18nPrefix}.syncing`) : t(`${i18nPrefix}.syncNow`)}
          </button>

          <button
            type="button"
            onClick={() => setDisconnectDialogOpen(true)}
            disabled={busy || working}
            className="w-full rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:border-destructive hover:text-destructive disabled:opacity-50"
          >
            {t(`${i18nPrefix}.connections.disconnectButton`)}
          </button>
        </div>
      )}

      {needsReconnect && (
        <div className="space-y-3">
          <p className="text-sm text-destructive">{t(`${i18nPrefix}.expired`)}</p>
          <button
            type="button"
            onClick={() => { void runAction(onConnect); }}
            disabled={connecting || busy || working}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-medium text-primary-foreground disabled:opacity-50"
          >
            {connecting && <Loader2 className="h-4 w-4 animate-spin" />}
            {connecting ? t(`${i18nPrefix}.connecting`) : t("connections.ux.connect")}
          </button>
          <button
            type="button"
            onClick={() => setDisconnectDialogOpen(true)}
            disabled={busy || working}
            className="w-full rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground hover:border-destructive hover:text-destructive"
          >
            {t(`${i18nPrefix}.connections.disconnectButton`)}
          </button>
        </div>
      )}

      {pendingInFlight && (
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-muted-foreground">{t("connections.pending.inProgress")}</p>
          <button
            type="button"
            onClick={() => { void runAction(onConnect); }}
            disabled={connecting || busy || working}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-medium text-primary-foreground disabled:opacity-50"
          >
            {connecting && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("connections.pending.retry")}
          </button>
          <button
            type="button"
            onClick={() => setDisconnectDialogOpen(true)}
            disabled={busy || working}
            className="w-full rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground"
          >
            {t("connections.pending.discard")}
          </button>
        </div>
      )}

      {pendingDead && (
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-muted-foreground">{t("connections.pending.expired")}</p>
          <button
            type="button"
            onClick={() => { void runAction(onConnect); }}
            disabled={connecting || busy || working}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-medium text-primary-foreground disabled:opacity-50"
          >
            {connecting && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("connections.ux.connect")}
          </button>
          <button
            type="button"
            onClick={() => setDisconnectDialogOpen(true)}
            disabled={busy || working}
            className="w-full rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground"
          >
            {t("connections.pending.discard")}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <motion.article
        data-provider-card={provider}
        initial={reduceMotion ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
        className="group flex min-h-[156px] flex-col rounded-2xl border border-border/80 bg-card p-4 shadow-sm transition-colors hover:border-primary/30 sm:p-5"
      >
        <div className="flex min-w-0 items-start gap-3">
          {mark}
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h2 className="truncate text-base font-semibold text-foreground sm:text-base">{title}</h2>
              {healthyConnected && <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />}
              {attention && <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />}
            </div>
            <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{summary ?? description}</p>
          </div>
        </div>

        <div className="mt-auto flex items-end justify-between gap-3 pt-4">
          <span className={`text-xs font-medium ${attention ? "text-amber-500" : healthyConnected ? "text-emerald-500" : "text-muted-foreground"}`}>
            {t(statusKey)}
          </span>

          {stateUnavailable ? <span className="text-xs text-muted-foreground">{t("connections.ux.retryAbove")}</span> : !status ? (
            <button
              type="button"
              onClick={() => { void summaryConnect(); }}
              disabled={connecting || busy || working}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {connecting && <Loader2 className="h-4 w-4 animate-spin" />}
              {connecting ? t(`${i18nPrefix}.connecting`) : t("connections.ux.connect")}
            </button>
          ) : needsReconnect || pendingDead ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setManageOpen(true)}
                disabled={busy || working}
                className="inline-flex min-h-11 items-center gap-1 rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-50"
              >
                {t("connections.ux.manage")}
              </button>
              <button
                type="button"
                onClick={() => { void summaryConnect(); }}
                disabled={connecting || busy || working}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              >
                {connecting && <Loader2 className="h-4 w-4 animate-spin" />}
                {connecting ? t(`${i18nPrefix}.connecting`) : t("connections.ux.connect")}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setManageOpen(true)}
              disabled={busy || working}
              className="inline-flex min-h-11 items-center gap-1 rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-50"
            >
              {t("connections.ux.manage")}
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
        {actionError && !manageOpen && !disconnectDialogOpen && (
          <p role="alert" className="mt-3 text-sm text-destructive">{t(actionError)}</p>
        )}
      </motion.article>

      <ConnectionManageSurface
        open={manageOpen && !stateUnavailable}
        onOpenChange={setManageOpen}
        title={title}
        description={description}
        closeLabel={t("common.close")}
        brand={<ConnectionBrandMark provider={provider} size="lg" />}
      >
        {!stateUnavailable && details}
      </ConnectionManageSurface>

      <AlertDialog open={disconnectDialogOpen && !stateUnavailable} onOpenChange={(open) => { if (!working) setDisconnectDialogOpen(open); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t(`${i18nPrefix}.connections.disconnectConfirmTitle`)}</AlertDialogTitle>
            <AlertDialogDescription>{t(`${i18nPrefix}.connections.disconnectConfirmDesc`)}</AlertDialogDescription>
          </AlertDialogHeader>
          {actionError && <p role="alert" className="text-sm text-destructive">{t(actionError)}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={working}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={working || busy || stateUnavailable}
              onClick={(event) => { event.preventDefault(); void handleDisconnectConfirm(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t(`${i18nPrefix}.connections.disconnectButton`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
